import { useEffect, useSyncExternalStore } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getAdminProblemReport } from '../../../api/generated/problem-reports/problem-reports'
import { Button } from '../../../components/ui/button'
import { useT } from '../../../i18n'
import { useProblemReportState } from '../../../lib/problem-reporting/host'
import { reportQueryKey } from '../../../lib/problem-reporting/context'
import type { ReportAdminSurface } from '../ProblemReporter'
import { useReportRead } from '../own-data'
import { reportCopy } from '../copy'
import { adminCopy } from './copy'
import type { createAdminCommands } from './commands'
import AdminQueue from './AdminQueue'
import AdminActions from './AdminActions'
import ReportEvidence from './ReportEvidence'
type Props = ReportAdminSurface & {
  commands: ReturnType<typeof createAdminCommands>
}
export default function AdminReports(props: Props) {
  const host = useProblemReportState(),
    t = useT(reportCopy),
    a = useT(adminCopy)
  const state = useSyncExternalStore(
    props.commands.subscribe,
    props.commands.getState,
    props.commands.getState,
  )
  if (
    host.phase !== 'ready' ||
    host.identity?.shell !== 'admin' ||
    !host.policy?.can_review
  )
    return <p role="alert">{t.unavailableTarget}</p>
  const frozen = state.outcome === 'pending' || state.outcome === 'unknown'
  return (
    <div className="space-y-4">
      {frozen && state.id !== props.selectedReportId && (
        <div role="status" className="space-y-2">
          <p>{a.resume}</p>
          <Button
            variant="outline"
            onClick={() => props.onSelectReport(state.id!)}
          >
            {a.openPending}
          </Button>
        </div>
      )}
      {props.selectedReportId ? (
        <AdminDetail
          key={props.selectedReportId}
          {...props}
          id={props.selectedReportId}
        />
      ) : (
        <AdminQueue onSelect={props.onSelectReport} />
      )}
    </div>
  )
}
function AdminDetail({
  id,
  commands,
  onBack,
}: {
  id: string
  commands: ReturnType<typeof createAdminCommands>
  onBack: () => void
}) {
  const t = useT(reportCopy),
    a = useT(adminCopy),
    host = useProblemReportState(),
    client = useQueryClient()
  const state = useSyncExternalStore(
    commands.subscribe,
    commands.getState,
    commands.getState,
  )
  const query = useReportRead(['admin', 'detail', id], (options) =>
    getAdminProblemReport(id, options),
  )
  const outcome = state.id === id ? state.outcome : 'idle'
  useEffect(() => {
    if (outcome === 'saved' || outcome === 'conflict' || outcome === 'rejected')
      void client.invalidateQueries({ queryKey: reportQueryKey() })
  }, [client, outcome])
  if (query.isPending) return <p role="status">{t.loading}</p>
  if (query.isError || query.data?.status !== 200)
    return (
      <div className="space-y-3">
        <Button variant="ghost" onClick={onBack}>
          {t.back}
        </Button>
        <p role="alert">{t.unavailableTarget}</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {t.retry}
        </Button>
      </div>
    )
  const report = query.data.data
  const frozen = state.outcome === 'pending' || state.outcome === 'unknown'
  const manageable = Boolean(host.policy?.can_manage && host.policy.can_review)
  const writable = manageable && Boolean(host.policy?.enabled)
  const savedStale =
    outcome === 'saved' &&
    state.revision !== null &&
    report.revision < state.revision
  return (
    <div className="space-y-4 text-sm">
      <Button size="sm" variant="ghost" onClick={onBack}>
        {t.back}
      </Button>
      <div className="flex justify-between gap-3 font-medium">
        <h3>{report.reference}</h3>
        <span>{t[report.status]}</span>
      </div>
      {outcome === 'conflict' && (
        <div role="alert" className="space-y-2">
          <p>{a.conflict}</p>
          <Button
            variant="outline"
            onClick={() =>
              void client.invalidateQueries({ queryKey: reportQueryKey() })
            }
          >
            {a.refresh}
          </Button>
        </div>
      )}
      {outcome === 'rejected' && <p role="alert">{t.error}</p>}
      {outcome === 'saved' && <p role="status">{a.saved}</p>}
      {frozen && state.id === id && (
        <div className="space-y-2">
          <p role="status">{t.pending}</p>
          <Button
            disabled={outcome === 'pending' || !manageable}
            onClick={() => void commands.retry()}
          >
            {t.recover}
          </Button>
        </div>
      )}
      <ReportEvidence report={report} />
      {report.status === 'closed' ? (
        <p>{a.terminal}</p>
      ) : writable ? (
        <AdminActions
          report={report}
          commands={commands}
          disabled={frozen || query.isFetching || savedStale}
        />
      ) : (
        <p className="text-[var(--color-text-muted)]">{a.readonly}</p>
      )}
    </div>
  )
}
